import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { C } from "../lib/constants";
import { validateImageFile } from "../lib/helpers";
import { uploadPublicFile } from "../lib/storage";
import { Btn } from "./ui/Btn";
import { Inp } from "./ui/Inp";
import { TopNav } from "./ui/TopNav";
import { FileUploadCard } from "./ui/FileUpload";

// ─── SHARED IDENTITY LOADER ──────────────────────────────────────────────────
// Priority: perfiles > clientes > trabajadores. Never falls back to user_metadata.
const loadSharedIdentity = async (userId, authUser = null) => {
  if (!userId) return {};
  const [pRes, cRes, wRes] = await Promise.all([
    supabase.from("perfiles").select("nombre,telefono,correo,email,foto_url,avatar_url,ine_url").eq("user_id", userId).neq("archivado", true).single(),
    supabase.from("clientes").select("nombre,telefono,correo,email,foto_url,avatar,ine_url").eq("user_id", userId).maybeSingle(),
    supabase.from("trabajadores").select("nombre,telefono,correo,email,foto_url,avatar,ine_url").eq("user_id", userId).neq("archivado", true).maybeSingle(),
  ]);
  const p = pRes.data || {};
  const c = cRes.data || {};
  const w = wRes.data || {};
  const first = (...vals) => vals.find(v => v && String(v).trim()) || "";
  return {
    nombre:   first(p.nombre,   c.nombre,   w.nombre),
    telefono: String(first(p.telefono, c.telefono, w.telefono) || "").replace(/\D/g, ""),
    correo:   first(p.correo, p.email, c.correo, c.email, w.correo, w.email, authUser?.email),
    foto_url: first(p.foto_url, p.avatar_url, c.foto_url, c.avatar, w.foto_url, w.avatar),
    ine_url:  first(p.ine_url,  c.ine_url,  w.ine_url),
  };
};

// ─── REGISTER CLIENT ──────────────────────────────────────────────────────────
export const RegClient = ({setScreen, user, onRegistered}) => {
  const [step,          setStep]         = useState(1);
  const [saving,        setSaving]       = useState(false);
  const [errorMsg,      setErrorMsg]     = useState("");
  const [validationMsg, setValidationMsg]= useState("");
  const [uploadProg,    setUploadProg]   = useState("");
  const [precargando,   setPrecargando]  = useState(true);
  const [perfilCargado, setPerfilCargado]= useState(false);

  const [form, setForm] = useState({
    name:"", phone:"", email:"", city:"",
    photo: null, ine: null,
  });
  const upd = (k,v) => { setValidationMsg(""); setErrorMsg(""); setForm(f=>({...f,[k]:v})); };

  useEffect(() => {
    if (perfilCargado) return;
    if (!user?.id) { setPrecargando(false); setPerfilCargado(true); return; }
    (async () => {
      try {
        const { data: authCheck } = await supabase.auth.getUser();
        if (!authCheck?.user?.id || authCheck.user.id !== user.id) {
          setPrecargando(false); setPerfilCargado(true); return;
        }
        const identity = await loadSharedIdentity(user.id, authCheck.user);
        setForm(f => ({
          ...f,
          name:  identity.nombre   || f.name,
          phone: identity.telefono || f.phone,
          email: identity.correo   || authCheck.user.email || f.email,
          photo: identity.foto_url || null,
          ine:   identity.ine_url  || null,
        }));
      } catch {
        // Leave form blank on error — never fall back to user_metadata
        setForm(f => ({ ...f, email: user?.email || f.email }));
      } finally {
        setPrecargando(false);
        setPerfilCargado(true);
      }
    })();
  }, [user?.id]); // eslint-disable-line

  if (precargando) return (
    <div style={{minHeight:"100vh",background:"#F7F5F2",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center",color:"#9A9590"}}>
        <div style={{fontSize:36,marginBottom:10}}>⏳</div>
        <div style={{fontSize:14}}>Cargando tus datos...</div>
      </div>
    </div>
  );

  const validar = (sig) => {
    setValidationMsg("");
    if (sig === 2) {
      if (!form.name.trim())  return setValidationMsg("Escribe tu nombre completo.");
      if (!form.phone.trim()) return setValidationMsg("Escribe tu número de WhatsApp.");
      if (!form.email.trim()) return setValidationMsg("Escribe tu correo electrónico.");
    }
    if (sig === "guardar") {
      if (!form.photo) return setValidationMsg("Tu foto de perfil es obligatoria.");
      if (!form.ine)   return setValidationMsg("La foto de tu INE es obligatoria para tu seguridad.");
    }
    return true;
  };

  const goStep = (n) => { if (!validar(n)) return; setStep(n); try { window.scrollTo({top:0,behavior:"smooth"}); } catch {} };

  const guardarCliente = async () => {
    if (!user?.id) { setScreen("cliente-login"); return; }
    if (!validar("guardar")) return;
    setSaving(true); setErrorMsg("");
    try {
      const { data: existing } = await supabase
        .from("clientes")
        .select("user_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        // FIX F1-5: "home" no es una pantalla válida; el cliente va a su panel principal.
        onRegistered?.();
        setScreen("obra-activa");
        return;
      }

      let fotoUrl = "";
      let ineUrl  = "";

      // FIX F3-16: validar tipo y tamaño antes de subir.
      const photoErrC = validateImageFile(form.photo instanceof File ? form.photo : null, "Foto");
      const ineErrC   = validateImageFile(form.ine   instanceof File ? form.ine   : null, "INE");
      if (photoErrC) throw new Error(photoErrC);
      if (ineErrC)   throw new Error(ineErrC);

      setUploadProg("Subiendo foto de perfil...");
      if (form.photo instanceof File) {
        fotoUrl = await uploadPublicFile(form.photo, "fotos-perfil", user.id + "-perfil");
      }
      setUploadProg("Subiendo INE...");
      if (form.ine instanceof File) {
        ineUrl = await uploadPublicFile(form.ine, "documentos-identidad", user.id + "-ine");
      }
      setUploadProg("Guardando perfil...");

      const cleanPhone = form.phone.replace(/\D/g,"");
      const payload = {
        user_id:  user.id,
        nombre:   form.name.trim(),
        telefono: cleanPhone,
        correo:   form.email.trim(),
        email:    form.email.trim(),
        zona:     form.city.trim() || null,
        foto_url: fotoUrl || null,
        ine_url:  ineUrl  || null,
        ine_verificado: false,
        calificacion: 0,
        "reseñas": 0,
        trabajos: 0,
        status: "activo",
        verificado: false,
        archivado: false,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from("clientes").insert([payload]);
      if (error) throw error;

      await supabase.from("perfiles").upsert([Object.fromEntries(
        Object.entries({
          user_id:     user.id,
          nombre:      form.name.trim()  || undefined,
          telefono:    cleanPhone        || undefined,
          email:       form.email.trim() || undefined,
          correo:      form.email.trim() || undefined,
          role:        "cliente",
          rol:         "cliente",
          modo_activo: "cliente",
          updated_at:  new Date().toISOString(),
        }).filter(([, v]) => v !== undefined && v !== null && v !== "")
      )], { onConflict: "user_id" });

      // FIX F1-5: registro exitoso → panel de cliente, no pantalla inexistente "home".
      onRegistered?.();
      setScreen("obra-activa");
    } catch(e) {
      setErrorMsg(e?.message || "Error al guardar. Intenta de nuevo.");
    } finally {
      setSaving(false); setUploadProg("");
    }
  };


  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:40}}>
      <TopNav
        setScreen={setScreen}
        back={step > 1 ? ()=>setStep(s=>s-1) : "landing"}
        backLabel={step > 1 ? "Paso 1" : "Inicio"}
        title="Registro de Cliente"
        right={
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {[1,2].map(s=>(
              <div key={s} style={{width:28,height:6,borderRadius:4,background:s<=step?C.blue:C.faint,transition:"background 0.3s"}}/>
            ))}
          </div>
        }
      />
      <div style={{maxWidth:520,margin:"0 auto",padding:"28px 20px"}}>

        {/* Indicador */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"10px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
          {[{n:1,label:"Tus datos",icon:"👤"},{n:2,label:"Foto e INE",icon:"📸"}].map((s,i)=>(
            <div key={s.n} style={{display:"flex",alignItems:"center",gap:i<1?6:0,flex:i<1?1:"auto"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:step>=s.n?C.blue:C.faint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:step>=s.n?"#fff":C.muted,flexShrink:0}}>
                  {step>s.n?"✓":s.n}
                </div>
                <span style={{fontSize:12,fontWeight:700,color:step>=s.n?C.text:C.muted}}>{s.label}</span>
              </div>
              {i<1&&<div style={{flex:1,height:2,background:step>s.n?C.blue:C.faint,borderRadius:2,margin:"0 6px"}}/>}
            </div>
          ))}
        </div>

        {validationMsg && (
          <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"10px 14px",color:C.red,fontSize:13,marginBottom:16,fontWeight:700}}>⚠️ {validationMsg}</div>
        )}

        {/* ══ PASO 1 ══════════════════════════════════════════════════════════ */}
        {step === 1 && (
          <div>
            <div style={{marginBottom:22}}>
              <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:800,color:C.text,marginBottom:4}}>Crea tu perfil</div>
              <div style={{color:C.muted,fontSize:13}}>Gratis. Los maestros también te califican en Ficio.</div>
            </div>
            <Inp label="Nombre completo" placeholder="¿Cómo te llaman?" value={form.name} onChange={e=>upd("name",e.target.value)} icon="👤"/>
            <Inp label="WhatsApp" type="tel" placeholder="10 dígitos sin espacios" value={form.phone} onChange={e=>upd("phone",e.target.value)} icon="📱"/>
            <Inp label="Correo electrónico" type="email" placeholder="tu@correo.com" value={form.email} onChange={e=>upd("email",e.target.value)} icon="✉️"/>
            <Inp label="Ciudad" placeholder="Ej. Los Mochis" value={form.city} onChange={e=>upd("city",e.target.value)} icon="📍"/>
            <div style={{background:C.blueL,border:`1.5px solid ${C.blue}25`,borderRadius:14,padding:16,marginBottom:22}}>
              <div style={{color:C.blue,fontWeight:700,fontSize:13,marginBottom:8}}>🏠 Tu reputación como cliente también cuenta</div>
              {["Busca por oficio y ciudad en el directorio","Los maestros te califican al finalizar","Historial de trabajos verificados","Mediación y garantía Ficio"].map(t=>(
                <div key={t} style={{display:"flex",gap:8,marginBottom:6}}><span style={{color:C.blue}}>✓</span><span style={{color:C.sub,fontSize:13}}>{t}</span></div>
              ))}
            </div>
            <Btn full color={C.blue} onClick={()=>goStep(2)}>Continuar → Foto e INE</Btn>
          </div>
        )}

        {/* ══ PASO 2 ══════════════════════════════════════════════════════════ */}
        {step === 2 && (
          <div>
            <div style={{marginBottom:22}}>
              <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:800,color:C.text,marginBottom:4}}>Foto e INE</div>
              <div style={{color:C.muted,fontSize:13}}>Obligatorio. Protege tanto al cliente como al maestro.</div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              <div>
                <FileUploadCard id="client-photo" value={form.photo} onChange={f=>upd("photo",f)} icon="📸" title="Tu foto" sub="Cara visible · obligatorio"/>
                {!form.photo&&<div style={{color:C.red,fontSize:10,marginTop:4,textAlign:"center",fontWeight:700}}>Requerido</div>}
              </div>
              <div>
                <FileUploadCard id="client-ine" value={form.ine} onChange={f=>upd("ine",f)} icon="🪪" title="Foto de INE" sub="Frente visible · obligatorio"/>
                {!form.ine&&<div style={{color:C.red,fontSize:10,marginTop:4,textAlign:"center",fontWeight:700}}>Requerido</div>}
              </div>
            </div>

            <div style={{background:C.blueL,border:`1px solid ${C.blue}25`,borderRadius:12,padding:"12px 14px",marginBottom:18}}>
              <div style={{color:C.blue,fontWeight:700,fontSize:12,marginBottom:3}}>🔒 ¿Por qué pedimos tu INE?</div>
              <div style={{color:C.sub,fontSize:12,lineHeight:1.6}}>Tu INE nunca se comparte públicamente. Solo lo usa el equipo Ficio en caso de disputa o mediación. Es la misma seguridad que exigimos a los maestros.</div>
            </div>

            {errorMsg && <div style={{background:C.redL,border:`1px solid ${C.red}30`,borderRadius:12,padding:"10px 12px",color:C.red,fontSize:12,marginBottom:14}}>{errorMsg}</div>}

            {saving && uploadProg && (
              <div style={{background:C.blueL,border:`1px solid ${C.blue}30`,borderRadius:12,padding:"10px 14px",marginBottom:14,color:C.blue,fontSize:13,fontWeight:700,textAlign:"center"}}>⏳ {uploadProg}</div>
            )}

            <Btn full color={form.photo&&form.ine?C.blue:"#CCC"} onClick={guardarCliente}>
              {saving ? "Guardando..." : "Crear mi perfil →"}
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
};
